import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';

// Helper for JSON API requests (similar to chatSupport)
// Note: These API key endpoints use JSON format, not Transit
const jsonRequest = (path, options = {}) => {
  const url = typeof window !== 'undefined' ? `${window.location.origin}${path}` : path;
  return window.fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        const e = new Error(data.message || 'Request failed');
        Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

// API Key management
export const FETCH_API_KEYS_REQUEST = 'app/ProfileSettingsPage/FETCH_API_KEYS_REQUEST';
export const FETCH_API_KEYS_SUCCESS = 'app/ProfileSettingsPage/FETCH_API_KEYS_SUCCESS';
export const FETCH_API_KEYS_ERROR = 'app/ProfileSettingsPage/FETCH_API_KEYS_ERROR';

export const GENERATE_API_KEY_REQUEST = 'app/ProfileSettingsPage/GENERATE_API_KEY_REQUEST';
export const GENERATE_API_KEY_SUCCESS = 'app/ProfileSettingsPage/GENERATE_API_KEY_SUCCESS';
export const GENERATE_API_KEY_ERROR = 'app/ProfileSettingsPage/GENERATE_API_KEY_ERROR';

export const REVOKE_API_KEY_REQUEST = 'app/ProfileSettingsPage/REVOKE_API_KEY_REQUEST';
export const REVOKE_API_KEY_SUCCESS = 'app/ProfileSettingsPage/REVOKE_API_KEY_SUCCESS';
export const REVOKE_API_KEY_ERROR = 'app/ProfileSettingsPage/REVOKE_API_KEY_ERROR';

export const CLEAR_GENERATED_API_KEY = 'app/ProfileSettingsPage/CLEAR_GENERATED_API_KEY';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  // API Keys
  apiKeys: [],
  fetchApiKeysInProgress: false,
  fetchApiKeysError: null,
  generateApiKeyInProgress: false,
  generateApiKeyError: null,
  generatedApiKey: null,
  revokeApiKeyInProgress: false,
  revokeApiKeyError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    // API Keys
    case FETCH_API_KEYS_REQUEST:
      return {
        ...state,
        fetchApiKeysInProgress: true,
        fetchApiKeysError: null,
      };
    case FETCH_API_KEYS_SUCCESS:
      return {
        ...state,
        apiKeys: payload.apiKeys || [],
        fetchApiKeysInProgress: false,
      };
    case FETCH_API_KEYS_ERROR:
      return {
        ...state,
        fetchApiKeysInProgress: false,
        fetchApiKeysError: payload,
      };

    case GENERATE_API_KEY_REQUEST:
      return {
        ...state,
        generateApiKeyInProgress: true,
        generateApiKeyError: null,
        generatedApiKey: null,
      };
    case GENERATE_API_KEY_SUCCESS:
      return {
        ...state,
        generateApiKeyInProgress: false,
        generatedApiKey: payload,
      };
    case GENERATE_API_KEY_ERROR:
      return {
        ...state,
        generateApiKeyInProgress: false,
        generateApiKeyError: payload,
      };

    case REVOKE_API_KEY_REQUEST:
      return {
        ...state,
        revokeApiKeyInProgress: true,
        revokeApiKeyError: null,
      };
    case REVOKE_API_KEY_SUCCESS:
      return {
        ...state,
        revokeApiKeyInProgress: false,
        apiKeys: state.apiKeys.map(key =>
          key.keyId === payload.keyId
            ? { ...key, active: false, revokedAt: new Date().toISOString() }
            : key
        ),
      };
    case REVOKE_API_KEY_ERROR:
      return {
        ...state,
        revokeApiKeyInProgress: false,
        revokeApiKeyError: payload,
      };

    case CLEAR_GENERATED_API_KEY:
      return {
        ...state,
        generatedApiKey: null,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};

// ================ API Key Management ================ //

export const fetchApiKeysRequest = () => ({ type: FETCH_API_KEYS_REQUEST });
export const fetchApiKeysSuccess = payload => ({ type: FETCH_API_KEYS_SUCCESS, payload });
export const fetchApiKeysError = error => ({
  type: FETCH_API_KEYS_ERROR,
  payload: error,
  error: true,
});

export const fetchApiKeys = () => {
  return dispatch => {
    dispatch(fetchApiKeysRequest());

    return jsonRequest('/api/api-key/list', {
      method: 'GET',
    })
      .then(response => {
        dispatch(fetchApiKeysSuccess(response));
      })
      .catch(e => {
        dispatch(fetchApiKeysError(storableError(e)));
      });
  };
};

export const generateApiKeyRequest = () => ({ type: GENERATE_API_KEY_REQUEST });
export const generateApiKeySuccess = payload => ({ type: GENERATE_API_KEY_SUCCESS, payload });
export const generateApiKeyError = error => ({
  type: GENERATE_API_KEY_ERROR,
  payload: error,
  error: true,
});

export const generateApiKey = ({ name, environment }) => {
  return (dispatch, getState, sdk) => {
    dispatch(generateApiKeyRequest());

    return jsonRequest('/api/api-key/generate', {
      method: 'POST',
      body: JSON.stringify({ name, environment }),
    })
      .then(response => {
        dispatch(generateApiKeySuccess(response));
        // Refresh currentUser to get updated privateData with new API key
        return dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false }))
          .then(() => response);
      })
      .catch(e => {
        dispatch(generateApiKeyError(storableError(e)));
        throw e;
      });
  };
};

export const revokeApiKeyRequest = () => ({ type: REVOKE_API_KEY_REQUEST });
export const revokeApiKeySuccess = payload => ({ type: REVOKE_API_KEY_SUCCESS, payload });
export const revokeApiKeyError = error => ({
  type: REVOKE_API_KEY_ERROR,
  payload: error,
  error: true,
});

export const revokeApiKey = keyId => {
  return (dispatch, getState, sdk) => {
    dispatch(revokeApiKeyRequest());

    return jsonRequest('/api-key/revoke', {
      method: 'POST',
      body: JSON.stringify({ keyId }),
    })
      .then(response => {
        dispatch(revokeApiKeySuccess({ keyId }));
        // Refresh currentUser to get updated privateData with revoked key
        return dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false }));
      })
      .catch(e => {
        dispatch(revokeApiKeyError(storableError(e)));
      });
  };
};

export const clearGeneratedApiKey = () => ({ type: CLEAR_GENERATED_API_KEY });
